<template>
  <div class="admin-page event-report">
    <admin-page-header
      eyebrow="Analitik / Mobil"
      title="Event Akışı"
      description="Mobil aktiviteyi izleyin; sayfa, event ve kullanıcı raporlarından tek bir etkileşimin detayına inin."
    >
      <template #actions>
        <span class="text-caption text--secondary">{{ lastUpdated ? `Güncellendi: ${formatTime(lastUpdated)}` : 'Türkiye saati (UTC+3)' }}</span>
        <v-btn
          outlined
          color="primary"
          small
          :loading="loading || overviewLoading"
          @click="refresh"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>
    <v-card
      outlined
      class="admin-table-card event-report__navigation"
    >
      <v-tabs
        v-model="activeTab"
        show-arrows
        aria-label="Analytics raporları"
      >
        <v-tab
          v-for="report in reports"
          :key="report.key"
        >
          {{ report.label }}
        </v-tab>
      </v-tabs>
    </v-card>
    <v-card
      outlined
      class="admin-table-card event-report__realtime"
      :aria-busy="realtimeLoading"
      aria-label="Yakın zamanlı kullanıcı aktivitesi"
    >
      <div class="event-report__realtime-count">
        <span
          class="event-report__realtime-dot"
          :class="{ 'event-report__realtime-dot--unavailable': !realtime }"
          aria-hidden="true"
        ></span>
        <span>Son 5 dakikada aktif</span>
        <strong
          role="status"
          aria-live="polite"
        >{{ realtime ? formatNumber(realtime.activeUsers) : '—' }}</strong>
        <v-progress-circular
          v-if="realtimeLoading"
          indeterminate
          size="16"
          width="2"
          color="primary"
        ></v-progress-circular>
      </div>
      <div class="event-report__realtime-note text-caption text--secondary">
        <div>Tarih aralığından bağımsız; diğer uygulanmış filtreler geçerli. Çevrimiçi durum değil, yakın zamanlı aktivitedir.</div>
        <span
          v-if="realtimeError"
          class="error--text"
          role="alert"
        >{{ realtimeError }}</span>
        <span v-else>30 saniyede bir yenilenir<span v-if="realtime"> · Son ölçüm: {{ formatTime(realtime.generatedAt) }} (TR)</span></span>
      </div>
      <v-btn
        v-if="realtimeError"
        small
        text
        color="primary"
        :loading="realtimeLoading"
        @click="fetchRealtime()"
      >
        Sayacı tekrar yükle
      </v-btn>
    </v-card>
    <v-card
      outlined
      class="admin-table-card event-report__filters"
    >
      <div
        v-if="currentReport !== 'realtime'"
        class="event-report__range"
      >
        <div
          class="event-report__presets"
          aria-label="Hızlı tarih aralığı"
        >
          <v-btn
            v-for="range in quickRanges"
            :key="range.days"
            small
            :text="quickRange !== range.days"
            :color="quickRange === range.days ? 'primary' : undefined"
            :outlined="quickRange === range.days"
            @click="setRange(range.days)"
          >
            {{ range.label }}
          </v-btn>
        </div>
        <div class="event-report__dates">
          <v-text-field
            v-model="filters.from"
            type="datetime-local"
            label="Başlangıç (TR)"
            outlined
            dense
            hide-details
            @input="quickRange = null"
          ></v-text-field>
          <span
            class="text--secondary"
            aria-hidden="true"
          >/</span>
          <v-text-field
            v-model="filters.to"
            type="datetime-local"
            label="Bitiş (TR)"
            outlined
            dense
            hide-details
            @input="quickRange = null"
          ></v-text-field>
        </div>
      </div>
      <div
        v-else
        class="event-report__range"
      >
        <strong class="text-body-2">Canlı rapor · Son 30 dakika</strong>
        <span class="text-caption text--secondary">Tarih seçimi bu raporda kullanılmaz. Diğer filtreler uygulanır.</span>
      </div>
      <v-divider></v-divider>
      <form
        class="event-report__filter-form"
        @submit.prevent="applyFilters"
      >
        <div class="event-report__filter-grid">
          <v-text-field
            v-model.trim="filters.userSearch"
            label="Kullanıcı"
            placeholder="ID, ad soyad veya e-posta"
            clearable
            outlined
            dense
            hide-details
          ></v-text-field>
          <v-select
            v-model="filters.eventType"
            :items="eventTypes"
            label="Event tipi"
            clearable
            outlined
            dense
            hide-details
          ></v-select>
          <v-combobox
            v-model="filters.eventName"
            :search-input.sync="filterSearch.eventName"
            :items="metadata.eventNames"
            :return-object="false"
            label="Event adı"
            clearable
            outlined
            dense
            hide-details
            @input="onFilterInput('eventName', $event)"
          ></v-combobox>
          <v-combobox
            v-model="filters.screenName"
            :search-input.sync="filterSearch.screenName"
            :items="metadata.screenNames"
            :return-object="false"
            label="Ekran"
            clearable
            outlined
            dense
            hide-details
            @input="onFilterInput('screenName', $event)"
          ></v-combobox>
          <v-select
            v-model="filters.platform"
            :items="platforms"
            label="Platform"
            clearable
            outlined
            dense
            hide-details
          ></v-select>
        </div>
        <div class="event-report__filter-actions">
          <admin-saved-filters
            storage-key="analytics-events"
            :value="filters"
            @apply="restoreSavedFilters"
          />
          <span
            class="text-caption"
            :class="filtersDirty ? 'warning--text' : 'text--secondary'"
          >{{ filtersDirty ? 'Değişen filtreleri rapora uygulayın.' : 'Tüm raporlar aynı uygulanmış filtreleri kullanır.' }}<span v-if="applied.exactEvent || applied.exactScreen"> · Rapordan seçilen alan tam eşleşir.</span></span>
          <v-spacer></v-spacer><v-btn
            text
            small
            @click="clearFilters"
          >
            Temizle
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            small
            :loading="loading || overviewLoading"
          >
            Uygula
          </v-btn>
        </div>
      </form>
    </v-card>
    <v-alert
      v-if="validationError"
      type="warning"
      dense
      outlined
      role="alert"
    >
      {{ validationError }}
    </v-alert>
    <v-alert
      v-if="overviewError && currentReport === 'overview'"
      type="error"
      dense
      outlined
      role="alert"
    >
      {{ overviewError }} <v-btn
        small
        text
        @click="fetchOverview"
      >
        Özeti tekrar yükle
      </v-btn>
    </v-alert>
    <section
      v-if="currentReport === 'overview'"
      aria-label="Aktivite özeti"
      :aria-busy="overviewLoading"
      class="event-report__overview"
    >
      <div class="event-report__section-label">
        <h2>Aktivite özeti</h2><span class="text-caption text--secondary">{{ appliedRangeLabel }} · UTC+3</span>
      </div>
      <v-card
        outlined
        class="event-report__metrics"
      >
        <div
          v-for="metric in metrics"
          :key="metric.label"
          class="event-report__metric"
        >
          <span class="event-report__metric-label">{{ metric.label }}</span>
          <v-skeleton-loader
            v-if="overviewLoading"
            type="text"
            width="65%"
          ></v-skeleton-loader>
          <strong v-else>{{ overview ? formatNumber(metric.value) : '—' }}</strong>
          <span class="text-caption text--secondary">{{ metric.note }}</span>
        </div>
      </v-card>
      <div class="event-report__definition text-caption text--secondary">
        Aktif kullanıcı: ekran, etkileşim veya oturum başlangıcı gönderen benzersiz kullanıcı ID'si.
        Anonim ve yalnızca arka plan/API eventleri aktif kullanıcı sayılmaz.
        DAU/WAU/MAU, {{ overview ? formatDay(overview.anchorDate) : 'bitiş günü' }} için seçilen bitiş anına kadar 1/7/30 Türkiye takvim gününü kapsar. Bugün seçiliyse mevcut saate kadar hesaplanır.
        <span v-if="overview">{{ formatNumber(overview.summary.anonymousEvents) }} anonim event seçili dönem toplamına dahildir.</span>
      </div>
    </section>
    <div
      v-if="currentReport === 'overview'"
      class="event-report__charts"
    >
      <v-card
        outlined
        class="admin-table-card event-report__trend"
      >
        <div class="event-report__card-heading">
          <div><h2>Günlük aktivite</h2><span class="text-caption text--secondary">Günlük tekil kullanıcılar toplanarak dönem kullanıcısı hesaplanmaz.</span></div>
          <v-btn-toggle
            v-model="chartMetric"
            mandatory
            dense
            borderless
            color="primary"
            aria-label="Grafik metriği"
          >
            <v-btn
              small
              value="activeUsers"
            >
              Kullanıcı
            </v-btn><v-btn
              small
              value="events"
            >
              Event
            </v-btn>
          </v-btn-toggle>
        </div>
        <v-skeleton-loader
          v-if="overviewLoading"
          type="image"
          class="ma-5"
        ></v-skeleton-loader>
        <vue-apex-charts
          v-else-if="overview && overview.summary.events"
          type="area"
          height="245"
          :options="chartOptions"
          :series="chartSeries"
        ></vue-apex-charts>
        <div
          v-else
          class="event-report__empty"
        >
          {{ overviewError ? 'Grafik verisi yüklenemedi.' : 'Bu aralıkta gösterilecek aktivite yok.' }}
        </div>
      </v-card>
      <v-card
        outlined
        class="admin-table-card event-report__ranking"
      >
        <div class="event-report__card-heading">
          <h2>En sık eventler</h2><span class="text-caption text--secondary">İlk 6</span>
        </div>
        <v-skeleton-loader
          v-if="overviewLoading"
          type="list-item-three-line@3"
          class="mx-4"
        ></v-skeleton-loader>
        <template v-else-if="overview && overview.topEvents.length">
          <div class="event-report__rank-head text-caption text--secondary">
            <span>Event adı</span><span>Adet</span>
          </div>
          <button
            v-for="event in overview.topEvents"
            :key="event.name"
            type="button"
            class="event-report__rank"
            :title="`${event.name} filtresini uygula`"
            @click="filterEvent(event.name)"
          >
            <span class="event-report__rank-name">{{ event.name }}</span><strong>{{ formatNumber(event.events) }}</strong>
            <span
              class="event-report__rank-bar"
              :style="{ width: `${100 * event.events / overview.topEvents[0].events}%` }"
            ></span>
          </button>
        </template>
        <div
          v-else
          class="event-report__empty"
        >
          {{ overviewError ? 'Event özeti yüklenemedi.' : 'Event bulunamadı.' }}
        </div>
      </v-card>
    </div>
    <analytics-dimension-report
      v-if="currentReport === 'overview'"
      key="overview-screens"
      dimension="screens"
      compact
      :filters="applied"
      :refresh-key="reportRefresh"
      @inspect="inspectReport"
      @expand="activeTab = 2"
    ></analytics-dimension-report>
    <div
      v-if="currentReport === 'overview'"
      class="event-report__shortcuts"
    >
      <button
        v-for="link in reportLinks"
        :key="link.tab"
        type="button"
        @click="activeTab = link.tab"
      >
        <span class="text-caption text--secondary">{{ link.eyebrow }}</span><strong>{{ link.title }} <span aria-hidden="true">→</span></strong><span class="text-caption text--secondary">{{ link.description }}</span>
      </button>
    </div>
    <analytics-realtime-report
      v-if="currentReport === 'realtime'"
      :filters="applied"
      :refresh-key="reportRefresh"
      @inspect="inspectReport"
    ></analytics-realtime-report>
    <analytics-dimension-report
      v-if="currentReport === 'screens' || currentReport === 'users'"
      :key="currentReport"
      :dimension="currentReport"
      :filters="applied"
      :refresh-key="reportRefresh"
      @inspect="inspectReport"
    ></analytics-dimension-report>
    <div
      v-if="currentReport === 'events'"
      class="event-report__section-label"
    >
      <v-btn
        v-if="drillOrigin"
        text
        small
        color="primary"
        @click="returnToReport"
      >
        {{ reports[drillOrigin.activeTab].label }} raporuna dön
      </v-btn>
      <div><h2>Event analizi</h2><span class="text-caption text--secondary">{{ appliedRangeLabel }} · Türkiye saati</span></div>
      <v-btn-toggle
        v-model="eventMode"
        mandatory
        dense
        color="primary"
        aria-label="Event raporu görünümü"
      >
        <v-btn
          small
          value="summary"
        >
          Event özeti
        </v-btn><v-btn
          small
          value="records"
        >
          Event kayıtları
        </v-btn>
      </v-btn-toggle>
    </div>
    <analytics-dimension-report
      v-if="currentReport === 'events' && eventMode === 'summary'"
      key="events-summary"
      dimension="events"
      :filters="applied"
      :refresh-key="reportRefresh"
      @inspect="inspectReport"
    ></analytics-dimension-report>
    <v-card
      v-if="currentReport === 'events' && eventMode === 'records'"
      outlined
      class="admin-table-card event-report__table"
    >
      <div class="event-report__card-heading">
        <div><h2>Event kayıtları <span class="event-report__count">{{ formatNumber(totalItems) }}</span></h2><span class="text-caption text--secondary">Tüm eşleşen kayıtlar · Bir satıra tıklayarak ayrıntıları inceleyin.</span></div>
        <v-select
          v-model="sortDirection"
          :items="sortOptions"
          label="Zaman sırası"
          dense
          outlined
          hide-details
          class="event-report__sort"
          @change="changePageSize"
        ></v-select>
      </div>
      <v-alert
        v-if="errorMessage"
        type="error"
        dense
        text
        class="ma-4"
        role="alert"
      >
        {{ errorMessage }} <v-btn
          small
          text
          @click="fetchEvents"
        >
          Tekrar dene
        </v-btn>
      </v-alert>
      <v-data-table
        :headers="headers"
        :items="events"
        :loading="loading"
        :items-per-page="pageSize"
        :server-items-length="totalItems"
        hide-default-footer
        :item-class="rowClass"
        :mobile-breakpoint="760"
        class="admin-data-table event-report__data"
        loading-text="Eventler yükleniyor…"
        @click:row="openDetail"
      >
        <template #[`item.eventName`]="{ item }">
          <button
            class="event-report__event-button"
            type="button"
            @click.stop="openDetail(item)"
          >
            {{ item.eventName || 'Adsız event' }}
          </button><div class="text-caption text--secondary">
            {{ eventTypeLabel(item.eventType) }}
          </div>
        </template>
        <template #[`item.user`]="{ item }">
          <div class="font-weight-medium">
            {{ item.userId ? `#${item.userId}` : 'Anonim' }}<span
              v-if="item.userName"
              class="ml-2 text-caption"
            >{{ item.userName }}</span>
          </div><div class="event-report__email text-caption text--secondary">
            {{ item.email || (item.userId ? 'E-posta eşleşmesi yok' : 'Oturum açılmamış') }}
          </div>
        </template>
        <template #[`item.screenName`]="{ item }">
          <div class="event-report__screen">
            {{ contextLabel(item) }}
          </div><div class="text-caption text--secondary">
            {{ item.screenName || 'Ekran belirtilmemiş' }}
          </div>
        </template>
        <template #[`item.platform`]="{ item }">
          <span class="event-report__platform">{{ platformLabel(item.platform) }}</span><div class="text-caption text--secondary">
            {{ item.appVersion || 'Sürüm yok' }}<span v-if="item.buildNumber"> ({{ item.buildNumber }})</span>
          </div>
        </template>
        <template #[`item.occurredAt`]="{ item }">
          <div class="event-report__numeric">
            {{ formatTime(item.occurredAt) }}
          </div><div class="text-caption text--secondary">
            {{ formatDay(item.occurredAt) }}
          </div>
        </template>
        <template #no-data>
          <div class="event-report__empty">
            <strong>{{ errorMessage ? 'Event listesi yüklenemedi' : 'Bu filtrelerle event bulunamadı' }}</strong><p class="mt-2">
              {{ errorMessage ? 'Bağlantıyı kontrol edip tekrar deneyin.' : 'Tarih aralığını genişletin veya filtreleri temizleyin.' }}
            </p><v-btn
              v-if="!errorMessage"
              small
              outlined
              color="primary"
              @click="clearFilters"
            >
              Filtreleri temizle
            </v-btn>
          </div>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <div class="event-report__pagination">
        <span
          class="text-caption text--secondary"
          aria-live="polite"
        >{{ pageSummary }}</span><v-spacer></v-spacer>
        <v-select
          v-model="pageSize"
          :items="[10, 25, 50, 100]"
          label="Satır / sayfa"
          dense
          outlined
          hide-details
          class="event-report__page-size"
          @change="changePageSize"
        ></v-select>
        <v-btn
          small
          text
          :disabled="page === 0 || loading"
          @click="goToPage(page - 1)"
        >
          Önceki
        </v-btn><span class="text-caption">{{ page + 1 }} / {{ pageCount }}</span>
        <v-btn
          small
          text
          :disabled="page + 1 >= pageCount || loading"
          @click="goToPage(page + 1)"
        >
          Sonraki
        </v-btn>
      </div>
    </v-card>
    <v-dialog
      v-model="detailOpen"
      max-width="820"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
    >
      <v-card
        v-if="selectedEvent"
        class="event-report__detail"
      >
        <v-card-title>
          <div>
            <div class="text-caption text--secondary">
              EVENT #{{ selectedEvent.id }}
            </div>{{ selectedEvent.eventName }}
          </div><v-spacer></v-spacer><v-btn
            icon
            aria-label="Event detayını kapat"
            @click="detailOpen = false"
          >
            <v-icon>{{ icons.close }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <dl class="event-report__details-grid">
            <div
              v-for="field in detailFields"
              :key="field.label"
            >
              <dt>{{ field.label }}</dt><dd>{{ field.value || '—' }}</dd>
            </div>
          </dl>
          <div class="event-report__card-heading px-0">
            <h2>Event özellikleri</h2><v-switch
              v-model="rawProperties"
              dense
              hide-details
              label="Ham JSON"
            ></v-switch>
          </div>
          <pre
            v-if="rawProperties"
            class="event-report__json"
          >{{ prettyProperties(selectedEvent) }}</pre>
          <dl
            v-else-if="propertyEntries.length"
            class="event-report__properties"
          >
            <div
              v-for="field in propertyEntries"
              :key="field.key"
            >
              <dt>{{ field.key }}</dt><dd>{{ field.value }}</dd>
            </div>
          </dl>
          <p
            v-else
            class="text--secondary"
          >
            Bu event için ek özellik gönderilmemiş.
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="selectedEvent.userId"
            text
            color="primary"
            :to="{ name: 'userList', query: { userId: String(selectedEvent.userId) } }"
          >
            Kullanıcı kaydı
          </v-btn>
          <v-spacer></v-spacer><v-btn
            v-if="selectedEvent.userId"
            text
            color="primary"
            @click="filterUser(selectedEvent.userId)"
          >
            Bu kullanıcının eventleri
          </v-btn><v-btn
            text
            @click="detailOpen = false"
          >
            Kapat
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import { mdiClose } from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminSavedFilters from '@/components/admin/AdminSavedFilters.vue'
import AnalyticsDimensionReport from '@/components/analytics/AnalyticsDimensionReport.vue'
import AnalyticsRealtimeReport from '@/components/analytics/AnalyticsRealtimeReport.vue'
import {
  eventRange, eventParams, eventProperties, validateEventRange, formatEventDate,
} from '@/utils/analyticsEvents'

const defaultFilters = () => ({
  ...eventRange(7),
  eventType: '',
  eventName: '',
  screenName: '',
  userSearch: '',
  platform: '',
  exactEvent: false,
  exactScreen: false,
})

export default {
  components: {
    AdminPageHeader, AdminSavedFilters, VueApexCharts, AnalyticsDimensionReport, AnalyticsRealtimeReport,
  },
  data: () => ({
    activeTab: 0,
    eventMode: 'summary',
    drillOrigin: null,
    reportRefresh: 0,
    reports: [{ key: 'overview', label: 'Genel Bakış' }, { key: 'realtime', label: 'Gerçek Zamanlı' }, { key: 'screens', label: 'Sayfalar ve Ekranlar' }, { key: 'events', label: 'Eventler' }, { key: 'users', label: 'Kullanıcılar' }],
    reportLinks: [
      {
        tab: 1, eyebrow: 'ŞİMDİ', title: 'Gerçek zamanlı aktivite', description: 'Dakika bazında aktivite, ekranlar ve son aktif kullanıcılar.',
      },
      {
        tab: 3, eyebrow: 'ETKİLEŞİM', title: 'Eventleri inceleyin', description: 'Event hacmini karşılaştırın ve tekil kayıtları açın.',
      },
      {
        tab: 4, eyebrow: 'KİTLE', title: 'Kullanıcıları keşfedin', description: 'ID ve e-posta ile kullanıcı bazında aktiviteyi izleyin.',
      },
    ],
    filters: defaultFilters(),
    filterSearch: { eventName: null, screenName: null },
    applied: defaultFilters(),
    quickRange: 7,
    quickRanges: [{ days: 1, label: 'Bugün' }, { days: 7, label: 'Son 7 gün' }, { days: 30, label: 'Son 30 gün' }, { days: 90, label: 'Son 90 gün' }],
    loading: false,
    overviewLoading: false,
    realtimeLoading: false,
    errorMessage: '',
    overviewError: '',
    realtimeError: '',
    validationError: '',
    page: 0,
    pageSize: 25,
    sortDirection: 'desc',
    totalItems: 0,
    events: [],
    overview: null,
    realtime: null,
    metadata: { eventNames: [], screenNames: [] },
    lastUpdated: null,
    chartMetric: 'activeUsers',
    selectedEvent: null,
    detailOpen: false,
    rawProperties: false,
    listRequest: 0,
    overviewRequest: 0,
    realtimeRequest: 0,
    listCancel: null,
    overviewCancel: null,
    realtimeCancel: null,
    realtimeTimer: null,
    icons: { close: mdiClose },
    sortOptions: [{ text: 'En yeni önce', value: 'desc' }, { text: 'En eski önce', value: 'asc' }],
    platforms: [{ text: 'iOS', value: 'ios' }, { text: 'Android', value: 'android' }, { text: 'Web', value: 'web' }],
    eventTypes: [
      { text: 'Ekran görüntüleme', value: 'SCREEN_VIEW' }, { text: 'Buton / CTA', value: 'CTA_CLICK' },
      { text: 'Modal açılışı', value: 'MODAL_OPEN' }, { text: 'Modal kapanışı', value: 'MODAL_CLOSE' },
      { text: 'Form gönderimi', value: 'FORM_SUBMIT' }, { text: 'İşlem başarılı', value: 'BUSINESS_SUCCESS' },
      { text: 'İşlem hatası', value: 'BUSINESS_FAIL' }, { text: 'API başarılı', value: 'API_SUCCESS' },
      { text: 'API hatası', value: 'API_FAIL' }, { text: 'Sistem', value: 'SYSTEM' }, { text: 'Kimlik', value: 'IDENTIFY' },
    ],
    headers: [
      {
        text: 'Event', value: 'eventName', width: '25%', sortable: false,
      },
      {
        text: 'Kullanıcı', value: 'user', width: '25%', sortable: false,
      },
      {
        text: 'Ekran / bağlam', value: 'screenName', width: '24%', sortable: false,
      },
      {
        text: 'Platform', value: 'platform', width: '12%', sortable: false,
      },
      {
        text: 'Zaman (TR)', value: 'occurredAt', width: '14%', sortable: false,
      },
    ],
  }),
  computed: {
    currentReport() { return this.reports[this.activeTab]?.key || 'overview' },
    requestOptions() { return { headers: { Authorization: `Bearer ${getAccessToken()}` }, timeout: 30000 } },
    filtersDirty() { return JSON.stringify(this.filters) !== JSON.stringify(this.applied) },
    pageCount() { return Math.max(1, Math.ceil(this.totalItems / this.pageSize)) },
    pageSummary() { return this.totalItems ? `${this.formatNumber(this.page * this.pageSize + 1)}–${this.formatNumber(Math.min((this.page + 1) * this.pageSize, this.totalItems))} / ${this.formatNumber(this.totalItems)} event` : '0 event' },
    appliedRangeLabel() { return `${this.formatDay(this.applied.from)} / ${this.formatDay(this.applied.to)}` },
    metrics() {
      const s = this.overview?.summary || {}

      return [
        { label: 'Toplam event', value: s.events, note: 'Seçili dönem' },
        { label: 'Aktif kullanıcı', value: s.activeUsers, note: 'Seçili dönemde tekil' },
        { label: 'Oturum', value: s.sessions, note: 'Seçili dönemde tekil oturum ID' },
        { label: 'DAU', value: s.dau, note: 'Son 1 gün · tekil kullanıcı' },
        { label: 'WAU', value: s.wau, note: 'Son 7 gün · tekil kullanıcı' },
        { label: 'MAU', value: s.mau, note: 'Son 30 gün · tekil kullanıcı' },
      ]
    },
    chartSeries() { return [{ name: this.chartMetric === 'events' ? 'Event' : 'Aktif kullanıcı', data: (this.overview?.trend || []).map(day => Number(day[this.chartMetric])) }] },
    chartOptions() {
      const { dark } = this.$vuetify.theme

      return {
        chart: {
          toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit', animations: { enabled: false },
        },
        theme: { mode: dark ? 'dark' : 'light' },
        colors: [this.$vuetify.theme.currentTheme.primary],
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.16, opacityTo: 0.02 } },
        grid: { borderColor: dark ? '#334155' : '#e2e8f0', strokeDashArray: 3, padding: { left: 12, right: 24 } },
        xaxis: {
          categories: (this.overview?.trend || []).map(day => day.date), labels: { hideOverlappingLabels: true, rotate: 0, formatter: value => this.formatDay(value).slice(0, 5) }, tickAmount: 6, axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: value => this.formatNumber(Math.round(value)) } },
        tooltip: { x: { formatter: (value, context) => this.formatDay((this.overview?.trend || [])[context.dataPointIndex]?.date) } },
      }
    },
    detailFields() {
      const e = this.selectedEvent || {}

      return [
        { label: 'Kullanıcı', value: e.userId ? `#${e.userId} ${e.userName || ''}` : 'Anonim' },
        { label: 'E-posta', value: e.email }, { label: 'Zaman (TR)', value: `${this.formatDay(e.occurredAt)} ${this.formatTime(e.occurredAt)}` },
        { label: 'Event tipi', value: this.eventTypeLabel(e.eventType) }, { label: 'Ekran', value: e.screenName },
        { label: 'Önceki ekran', value: e.previousScreenName }, { label: 'Oturum ID', value: e.sessionId },
        { label: 'Platform / sürüm', value: `${this.platformLabel(e.platform)} / ${e.appVersion || '—'}` },
        { label: 'Hedef', value: [e.targetType, e.targetId].filter(Boolean).join(' / ') },
        { label: 'Bileşen / adım', value: [e.componentName, e.stepKey].filter(Boolean).join(' / ') },
        { label: 'Kampanya / offer ID', value: [e.campaignId, e.offerId].filter(Boolean).join(' / ') },
        { label: 'Link ID', value: e.linkId },
      ]
    },
    propertyEntries() { return Object.entries(eventProperties(this.selectedEvent)).map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value) })) },
  },
  watch: {
    activeTab() { this.loadCurrentReport() },
    eventMode() { if (this.currentReport === 'events' && this.eventMode === 'records') this.fetchEvents() },
  },
  mounted() { this.refresh(); this.fetchMetadata(); this.startRealtimePolling() },
  beforeDestroy() {
    this.stopRealtimePolling()
    this.listRequest += 1
    this.overviewRequest += 1
    this.realtimeRequest += 1
    if (this.listCancel) this.listCancel.cancel()
    if (this.overviewCancel) this.overviewCancel.cancel()
    if (this.realtimeCancel) this.realtimeCancel.cancel()
  },
  methods: {
    restoreSavedFilters(value) { this.filters = { ...defaultFilters(), ...value }; this.resetFilterSearch(); this.applyFilters() },
    analyticsUrl(path) { return `${process.env.VUE_APP_BASE_API_URL}admin/analytics${path}` },
    async fetchMetadata() {
      try {
        const response = await axios.get(this.analyticsUrl('/metadata'), this.requestOptions)
        this.metadata = response.data.data || this.metadata
      } catch (_) { /* Free-text filtering remains available when suggestions are unavailable. */ }
    },
    async fetchEvents() {
      this.listRequest += 1
      const request = this.listRequest
      if (this.listCancel) this.listCancel.cancel()
      this.listCancel = axios.CancelToken.source()
      this.loading = true
      this.errorMessage = ''
      this.events = []
      try {
        const response = await axios.get(this.analyticsUrl('/events'), {
          ...this.requestOptions,
          cancelToken: this.listCancel.token,
          params: {
            ...eventParams(this.applied), page: this.page, size: this.pageSize, sort: `occurredAt,${this.sortDirection}`,
          },
        })
        if (request !== this.listRequest) return
        const payload = response.data.data || {}
        this.events = payload.content || []
        this.totalItems = Number(payload.totalElements || 0)
        this.lastUpdated = new Date().toISOString()
      } catch (error) {
        if (request !== this.listRequest || axios.isCancel(error)) return
        this.totalItems = 0
        this.errorMessage = error.response?.data?.message || 'Event listesi yüklenemedi.'
      } finally { if (request === this.listRequest) this.loading = false }
    },
    async fetchOverview() {
      this.overviewRequest += 1
      const request = this.overviewRequest
      if (this.overviewCancel) this.overviewCancel.cancel()
      this.overviewCancel = axios.CancelToken.source()
      this.overviewLoading = true
      this.overviewError = ''
      this.overview = null
      try {
        const response = await axios.get(this.analyticsUrl('/events/overview'), { ...this.requestOptions, cancelToken: this.overviewCancel.token, params: eventParams(this.applied) })
        if (request === this.overviewRequest) this.overview = response.data.data
      } catch (error) {
        if (request !== this.overviewRequest || axios.isCancel(error)) return
        this.overviewError = error.response?.data?.message || 'Aktivite özeti yüklenemedi. Event listesini kullanmaya devam edebilirsiniz.'
      } finally { if (request === this.overviewRequest) this.overviewLoading = false }
    },
    async fetchRealtime(clear = false) {
      this.realtimeRequest += 1
      const request = this.realtimeRequest
      if (this.realtimeCancel) this.realtimeCancel.cancel()
      this.realtimeCancel = axios.CancelToken.source()
      this.realtimeLoading = true
      this.realtimeError = ''
      if (clear) this.realtime = null
      try {
        const params = eventParams(this.applied)
        delete params.from
        delete params.to
        const response = await axios.get(this.analyticsUrl('/events/realtime'), {
          ...this.requestOptions, cancelToken: this.realtimeCancel.token, params,
        })
        if (request === this.realtimeRequest) this.realtime = response.data.data
      } catch (error) {
        if (request !== this.realtimeRequest || axios.isCancel(error)) return
        this.realtime = null
        this.realtimeError = error.response?.data?.message || 'Aktif kullanıcı sayacı güncellenemedi.'
      } finally { if (request === this.realtimeRequest) this.realtimeLoading = false }
    },
    startRealtimePolling() {
      if (this.realtimeTimer !== null) return
      this.realtimeTimer = setInterval(this.refreshRealtimeWhenVisible, 30000)
      document.addEventListener('visibilitychange', this.refreshRealtimeWhenVisible)
    },
    stopRealtimePolling() {
      if (this.realtimeTimer !== null) clearInterval(this.realtimeTimer)
      this.realtimeTimer = null
      document.removeEventListener('visibilitychange', this.refreshRealtimeWhenVisible)
    },
    refreshRealtimeWhenVisible() {
      if (!document.hidden && !this.realtimeLoading) this.fetchRealtime()
    },
    loadCurrentReport() {
      if (this.currentReport === 'overview') this.fetchOverview()
      if (this.currentReport === 'events' && this.eventMode === 'records') this.fetchEvents()
    },
    refresh() { this.reportRefresh += 1; this.loadCurrentReport(); this.fetchRealtime(true) },
    onFilterInput(field, value) {
      this.filterSearch[field] = value
      this.filters[field === 'eventName' ? 'exactEvent' : 'exactScreen'] = false
    },
    resetFilterSearch() {
      this.filterSearch = { eventName: this.filters.eventName || '', screenName: this.filters.screenName || '' }
    },
    syncFilterSearch() {
      // Combobox free text can still be waiting for a blur commit when the form submits.
      Object.entries(this.filterSearch).forEach(([field, value]) => {
        if (typeof value !== 'string' || value === (this.filters[field] || '')) return
        this.filters[field] = value
        this.onFilterInput(field, value)
      })
    },
    applyFilters() {
      this.syncFilterSearch()
      this.validationError = this.currentReport === 'realtime' ? '' : validateEventRange(this.filters)
      if (this.validationError) return
      this.applied = { ...this.filters }
      this.page = 0
      this.refresh()
    },
    clearFilters() { this.drillOrigin = null; this.filters = defaultFilters(); this.resetFilterSearch(); this.quickRange = 7; this.applyFilters() },
    setRange(days) { this.filters = { ...this.filters, ...eventRange(days) }; this.quickRange = days; this.applyFilters() },
    changePageSize() { this.page = 0; this.fetchEvents() },
    goToPage(page) { this.page = Math.max(0, page); this.fetchEvents() },
    filterEvent(name) { this.inspectReport({ dimension: 'events', key: name }) },
    filterUser(id) { this.detailOpen = false; this.inspectReport({ dimension: 'users', key: String(id) }) },
    inspectReport({
      dimension, key, windowStartedAt, generatedAt,
    }) {
      if (!this.drillOrigin) {
        this.drillOrigin = {
          activeTab: this.activeTab, eventMode: this.eventMode, filters: { ...this.applied }, quickRange: this.quickRange,
        }
      }
      this.filters = { ...this.applied }
      if (dimension === 'screens') { this.filters.screenName = key; this.filters.exactScreen = true }
      if (dimension === 'events') { this.filters.eventName = key; this.filters.exactEvent = true }
      if (dimension === 'users') this.filters.userSearch = String(key)
      if (windowStartedAt && generatedAt) {
        const localTime = value => new Date(new Date(value).getTime() + 10800000).toISOString().slice(0, 23)
        this.filters.from = localTime(windowStartedAt)
        this.filters.to = localTime(generatedAt)
        this.quickRange = null
      }
      this.eventMode = 'records'
      this.activeTab = 3
      this.resetFilterSearch()
      this.applyFilters()
    },
    returnToReport() {
      if (!this.drillOrigin) return
      const origin = this.drillOrigin
      this.drillOrigin = null
      this.filters = { ...origin.filters }; this.quickRange = origin.quickRange
      this.activeTab = origin.activeTab; this.eventMode = origin.eventMode
      this.resetFilterSearch()
      this.applyFilters()
    },
    openDetail(item) { this.selectedEvent = item; this.rawProperties = false; this.detailOpen = true },
    rowClass(item) { return this.detailOpen && this.selectedEvent?.id === item.id ? 'event-report__selected' : '' },
    contextLabel(item) {
      const p = eventProperties(item)

      return p.linkTitle || p.linkName || p.link_name || p.campaignTitle || p.campaignName || p.offerName || p.brandName || p.screenTitle || '—'
    },
    eventTypeLabel(type) { return this.eventTypes.find(item => item.value === type)?.text || type || '—' },
    platformLabel(value) { return this.platforms.find(item => item.value === String(value).toLowerCase())?.text || value || 'Bilinmiyor' },
    prettyProperties(item) { return JSON.stringify(eventProperties(item), null, 2) },
    formatTime(value) { return formatEventDate(value, true) },
    formatDay(value) { return formatEventDate(value) },
    formatNumber(value) { return new Intl.NumberFormat('tr-TR').format(Number(value || 0)) },
  },
}
</script>

<style lang="scss" scoped>
.event-report, .event-report__detail {
  --event-border: rgba(100, 116, 139, .16);
  --event-strong: #172033;
  h2 { margin: 0; font-size: 15px; font-weight: 700; color: var(--event-strong); }
  dt { font-size: 11px; font-weight: 600; opacity: .7; margin-bottom: 4px; }
  dd { margin: 0; font-size: 13px; overflow-wrap: anywhere; white-space: pre-wrap; }
}
.event-report {
  gap: 20px; min-width: 0;
  &__navigation { overflow: hidden; box-shadow: none !important; }
  &__shortcuts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
  &__shortcuts button { display: flex; flex-direction: column; gap: 8px; padding: 20px; text-align: left; color: inherit; border-top: 2px solid var(--event-border); }
  &__shortcuts button:hover, &__shortcuts button:focus-visible { border-color: var(--v-primary-base); background: rgba(100, 116, 139, .04); }
  &__shortcuts strong { font-size: 15px; color: var(--event-strong); }
  &__realtime { display: flex; align-items: center; flex-wrap: wrap; gap: 12px 24px; padding: 16px 20px; box-shadow: none !important; }
  &__realtime-count { display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 600; }
  &__realtime-count strong { font-size: 27px; line-height: 1.1; color: var(--event-strong); font-variant-numeric: tabular-nums; }
  &__realtime-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--v-success-base); }
  &__realtime-dot--unavailable { background: #94a3b8; }
  &__realtime-note { flex: 1 1 380px; line-height: 1.6; }
  &__range, &__filter-actions, &__card-heading, &__pagination, &__section-label { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  &__range { padding: 18px 20px; justify-content: space-between; }
  &__presets { display: flex; flex-wrap: wrap; gap: 4px; }
  &__dates { display: flex; align-items: center; gap: 10px; flex: 0 1 540px; min-width: 0; }
  &__filter-form { padding: 18px 20px 12px; }
  &__filter-grid { display: grid; grid-template-columns: 1.5fr 1fr 1.2fr 1.2fr .8fr; gap: 12px; }
  &__filter-grid > * { min-width: 0; }
  &__filter-actions { margin-top: 12px; }
  &__section-label { justify-content: space-between; margin-bottom: 12px; }
  &__metrics { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); box-shadow: none !important; }
  &__metric { padding: 20px; display: flex; flex-direction: column; gap: 7px; border-right: 1px solid var(--event-border); }
  &__metric:last-child { border-right: 0; }
  &__metric strong { color: var(--event-strong); font-size: 29px; line-height: 1.3; font-variant-numeric: tabular-nums; letter-spacing: -.04em; }
  &__metric-label { font-size: 12px; font-weight: 600; }
  &__definition { margin: 10px 2px 0; max-width: 1250px; line-height: 1.65; }
  &__charts { display: grid; grid-template-columns: minmax(0, 1.85fr) minmax(290px, 1fr); gap: 20px; }
  &__card-heading { padding: 18px 20px; justify-content: space-between; }
  &__rank-head { display: flex; justify-content: space-between; margin: 0 20px 6px; }
  &__rank { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 12px; width: calc(100% - 40px); padding: 10px 0; margin: 0 20px 6px; text-align: left; font-size: 12px; color: inherit; }
  &__rank:hover, &__rank:focus-visible { color: var(--v-primary-base); }
  &__rank-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__rank-bar { position: absolute; height: 2px; bottom: 0; left: 0; background: var(--v-primary-base); opacity: .3; }
  &__rank strong, &__numeric { font-variant-numeric: tabular-nums; }
  &__empty { min-height: 210px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 24px; text-align: center; font-size: 13px; }
  &__sort { max-width: 175px; }
  &__count { margin-left: 8px; color: var(--v-primary-base); font-variant-numeric: tabular-nums; }
  &__event-button { color: var(--event-strong); text-align: left; font-weight: 600; overflow-wrap: anywhere; }
  &__event-button:hover, &__event-button:focus-visible { color: var(--v-primary-base); text-decoration: underline; }
  &__email, &__screen { overflow-wrap: anywhere; }
  &__platform { font-size: 12px; font-weight: 600; }
  &__pagination { padding: 14px 20px; }
  &__page-size { max-width: 115px; }
  &__details-grid { margin-top: 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  &__properties > div { padding: 12px 0; border-bottom: 1px solid var(--event-border); display: grid; grid-template-columns: minmax(100px, 1fr) minmax(0, 2fr); gap: 20px; }
  &__json { font-size: 12px; padding: 16px; background: rgba(100, 116, 139, .08); border-radius: 8px; overflow-wrap: anywhere; white-space: pre-wrap; }
  &__detail .v-card__title { word-break: break-word; }
  ::v-deep .event-report__data td { padding-top: 13px !important; padding-bottom: 13px !important; }
  ::v-deep .event-report__data tbody tr { cursor: pointer; }
  ::v-deep .event-report__selected { background: rgba(212, 16, 40, .06) !important; }
  @media (max-width: 1200px) {
    &__filter-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    &__metrics { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    &__metric:nth-child(3) { border-right: 0; }
    &__metric:nth-child(-n+3) { border-bottom: 1px solid var(--event-border); }
  }
  @media (max-width: 760px) {
    &__shortcuts { grid-template-columns: 1fr; }
    &__charts { grid-template-columns: minmax(0, 1fr); }
    &__range, &__filter-form, &__card-heading, &__pagination { padding: 14px; }
    &__dates { flex-basis: 100%; flex-wrap: wrap; }
    &__dates > .v-input { flex: 1 1 100%; }
    &__dates > span { display: none; }
    &__filter-grid { grid-template-columns: minmax(0, 1fr); }
    &__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    &__metric { padding: 16px; }
    &__metric:nth-child(3) { border-right: 1px solid var(--event-border); }
    &__metric:nth-child(even) { border-right: 0; }
    &__metric:nth-child(-n+4) { border-bottom: 1px solid var(--event-border); }
    &__details-grid { grid-template-columns: 1fr; }
    &__properties > div { grid-template-columns: 1fr; gap: 4px; }
    &__pagination > span:first-child { flex-basis: 100%; }
  }
}
.theme--dark .event-report, .event-report__detail.theme--dark { --event-strong: #f1f5f9; }
</style>
